// @ts-strict-ignore
import isEqual from 'lodash/isEqual';
import intersectionBy from 'lodash/intersectionBy';
import unionBy from 'lodash/unionBy';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';
import produce from 'immer';
import matchesProperty from 'lodash/matchesProperty';
import sortBy from 'lodash/sortBy';
import {nanoid} from 'nanoid';

import {
  ALL_MATH_TYPES,
  PropertyType,
  ResourceType,
  ResourceType as PropertyFilterResourceType,
} from 'common/report/constants';
import {Behavior, CustomProperty, CustomPropertyResourceType, Segmentation} from 'common/types/reports/query';
import {assert, dataGroupIDsEqual} from 'common/util';
import {isBehavioralCustomProperty, isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {PropertyDefinition} from 'common/util/definitions-types';
import {propertiesUtils} from 'common/report/util/properties';
import {
  fetchLookupTableProperties,
  getTempLookupTableMappings,
  lookupTablesStore,
} from 'common/report/stores/lookup-tables';
import {assertUnreachable} from 'common/util/typescript';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {CohortStore} from 'common/report/stores/cohorts';
import {getSelectedPropertyName} from 'common/query-builder/query-block/utils/property-util';
import {getLabelForBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {
  getAllGlobalPropertyDefinitions,
  getAllPropertyDefinitionsForGroupProfiles,
  getAllPropertyDefinitionsForUserProfiles,
} from 'common/report/stores/property-definitions';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {getEventSelectedLabel} from 'common/query-builder/query-block/events-menu/util';
import {Event} from 'common/query-builder/query-block/events-menu/types';
import {Translatable} from 'common/i18n';
import {
  isSerializedInlineCohort,
  SerializedCohort,
  SerializedInlineCohort,
  User,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {Cohort as CohortModel} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {CohortResultItem} from 'common/widgets/inline-cohorts-modal/types';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {FiltersOperator} from 'common/query-builder/query-entry-section/types';
import {LocalizationStore} from 'common/i18n/store';
import {CustomPropertiesResource} from 'common/report/model-resources/custom-properties';
import {InlineCustomPropertiesStore} from 'common/report/stores/inline-custom-properties-store';

import {
  CohortItem,
  DataGroupItem,
  isEventSectionItem,
  isProperty,
  Item,
  MenuItemType,
  NonDataGroupPropertyItemIdentifierKeys,
  Property,
  PropertyItemPersistedIdentifierKeys,
  Selection,
  SerializedItemKeyMap,
  StagedItemType,
} from './types';
import {EventSectionItem, ObjectProperty, PropertySectionItem} from './item-menu-types';
import {EXTRA_PROPERTY_DEFINITIONS_BY_TYPE, TOP_PEOPLE} from './constants';

/**
 * Find intersection of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesIntersection(propertyLists: PropertySectionItem[]) {
  // @ts-ignore - TODO: Argument of type 'PropertyItem' is not assignable to parameter of type 'ArrayLike<string>'.
  return intersectionBy(...propertyLists, `name`);
}

/**
 * Find union of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesUnion(propertyLists: PropertySectionItem[]) {
  // @ts-ignore - TODO: Expected at least 1 arguments, but got 1 or more.
  return unionBy(...propertyLists, `name`);
}

export function sanitizeCustomProperty(customProperty) {
  return pick(customProperty, [
    `behavior`,
    `composedProperties`,
    `mappedDataGroupId`,
    `referencedDirectlyBy`,
    `referencedBy`,
    `referencedRawEventProperties`,
    `referencesBorrowedProperty`,
    `description`,
    `displayFormula`,
    `name`,
    `userInfo`,
    `resourceType`,
    `propertyType`,
    `joinPropertyType`,
    `scdEvent`,
    `scdOptions`,
    `allow_staff_override`,
    `canUpdateBasic`,
    `can_share`,
    `is_superadmin`,
    `pre_populated_entity_id`,
  ]);
}

export const CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING = {
  [CustomPropertyResourceType.Events]: PropertyFilterResourceType.Event,
  [CustomPropertyResourceType.People]: PropertyFilterResourceType.User,
};

/** including customPropertyId because it may represent the id of a non-persisted custom prop.*/
export function storeCustomPropertyToProperty(
  customPropertyId: string,
  customProperty: CustomProperty,
): PropertySectionItem {
  customProperty = sanitizeCustomProperty(customProperty);
  const {name} = customProperty;
  let {mappedDataGroupId, propertyType, joinPropertyType} = customProperty;

  let resourceType = CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType] || null;
  if (isBehavioralCustomProperty(customProperty)) {
    /** Behavioral Custom Properties are always User Properties */
    resourceType = ResourceType.User;
  }

  if (isPersistedCustomProperty(customPropertyId)) {
    const tempDataGroupId = getTempLookupTableMappings(resourceType, true)?.get(Number(customPropertyId));
    if (tempDataGroupId) {
      mappedDataGroupId = tempDataGroupId;
      joinPropertyType = joinPropertyType || propertyType;
      propertyType = PropertyType.Dimension;
    }
  }

  let menuItemType;
  switch (resourceType) {
    case ResourceType.Event:
      menuItemType = MenuItemType.EventCustomProperties;
      break;
    case ResourceType.User:
      menuItemType = MenuItemType.UserCustomProperties;
      break;
    case ResourceType.Cohort:
    case ResourceType.Other:
    case ResourceType.RuntimeProperty:
    case ResourceType.Formula:
    case ResourceType.Metric:
      throw Error(`Unexpected resource type in storeCustomPropertyToProperty: ${resourceType}`);
    case null:
      // TODO: This should really not happen and code should be cleaned up to ensure it doesn't
      menuItemType = MenuItemType.EventCustomProperties;
      break;
    default:
      assertUnreachable(resourceType);
  }

  const dataGroupId = getDataGroupIdForCustomProp(customProperty, resourceType);

  return {
    customProperty,
    customPropertyId,
    dataGroupId,
    dimensionDataGroupId: mappedDataGroupId,
    resourceType,
    propertyType,
    propertyDefaultType: propertyType,
    type: propertyType,
    joinPropertyType,
    value: name,
    label: name,
    menuItemType,
  };
}

/**
 * Compare if two properties are the same, ignoring typecast, changes in custom property definitions, etc...
 * NOTE: Even for inline custom properties, they must have the same temporary id to be considered the same.
 */
export function isSamePropertyReference(property1, property2): boolean {
  if (property1.customPropertyId || property2.customPropertyId) {
    return property1.customPropertyId === property2.customPropertyId;
  }
  const PROPERTY_KEY_FIELDS = [`resourceType`, `propertyName`];
  return isEqual(pick(property1, PROPERTY_KEY_FIELDS), pick(property2, PROPERTY_KEY_FIELDS));
}

/** @todo: [shawn] Join utils to a base type convertor */
export function propertyItemToObjectPropertyItem(propertyItem: PropertySectionItem): ObjectProperty {
  assert(isProperty(propertyItem));
  const {
    dataGroupId,
    resourceType,
    propertyDefaultType,
    propertyObjectKey,
    tempDataGroupId,
    joinPropertyType,
    scdEvent,
    type,
    displayOptions,
    value,
  } = propertyItem;

  return {
    ...(dataGroupId ? {dataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : {}),
    ...(scdEvent ? {scdEvent} : {}),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    type,
    displayOptions,
    resourceType,
    propertyDefaultType,
    propertyName: value,
    propertyType: type,
    ...(propertyObjectKey
      ? {
          propertyObjectKey,
        }
      : {}),
  };
}

/**
 * Generates a Segmentation format for user or event PropertyItem types
 * used in property menu. In general, Segmentation is what we use for bookmarks.
 *
 * TODO: Strongly type the input, theres a bug in the type of propertyItem
 * not transferring custom properties correctly.
 */
export function propertyItemToSegmentation(propertyItem, resourceType): Segmentation {
  const {dimensionDataGroupId, dataGroupId, value, type} = propertyItem;

  return {
    resourceType,
    propertyType: type,
    propertyName: value,
    ...(dataGroupId ? {dataGroupId} : dimensionDataGroupId && Array.isArray(value) ? {dimensionDataGroupId} : null),
    ...pick(
      propertyItem,
      `behavior`,
      `customPropertyId`,
      `customProperty`,
      `tempDataGroupId`,
      `propertyDefaultType`,
      `propertyObjectKey`,
      `joinPropertyType`,
      `listItemGroup`,
      `listItemFilters`,
      `unit`,
      `scdEvent`,
      `borrowEvent`,
      `scdOptions`,
    ),
  };
}

/**
 * Historically, we have been pushing 2 formats for defining properties
 * in the UI, as a result we have Segmentation and Property which are
 * almost identical with the exception of the following fields:
 *
 * `propertyType` <-> `type`
 *
 * `propertyName` <-> `value
 */
export function segmentationToPropertyItem(segment: Segmentation): Property {
  return {
    ...omit(segment, `propertyName`, `propertyType`),
    type: segment.propertyType,
    value: segment.propertyName,
  };
}

export function transformCohortToCohortItem(
  cohort: SerializedCohort | SerializedInlineCohort,
  cohortsStore: CohortStore,
): CohortItem {
  if (isSerializedInlineCohort(cohort as SerializedInlineCohort)) {
    const unsavedCohort = cohort as SerializedInlineCohort;

    // Hide converted cohorts if the saved cohort exists in store
    if (
      unsavedCohort.raw_cohort.unsavedId &&
      Number.isInteger(unsavedCohort.id) &&
      cohortsStore.value[unsavedCohort.id]
    ) {
      return null;
    }

    // Unsaved cohort does not contain permissions (e.g. canView)
    return {
      label: unsavedCohort.name,
      name: unsavedCohort.name,
      value: {
        cohort: {
          ...unsavedCohort,
        },
      },
      // https://mixpanel.atlassian.net/browse/SRFE-4562
      // but they should always be available for share
      canView: true,
      createdBy: unsavedCohort.created_by,
    };
  }

  const savedCohort = cohort as SerializedCohort;

  return {
    id: savedCohort.id,
    label: savedCohort.name,
    value: savedCohort.id,
    name: savedCohort.name,
    count: savedCohort.count,
    description: savedCohort.description,
    dataGroupId: savedCohort.data_group_id,
    createdBy: savedCohort.created_by,
    lastEdited: savedCohort.last_edited,
    can_update_basic: savedCohort.can_update_basic,
    canView: savedCohort.can_view,
    canUpdateBasic: savedCohort.can_update_basic,
    canShare: savedCohort.can_share,
    isSuperadmin: savedCohort.is_superadmin,
    allowStaffOverride: savedCohort.allow_staff_override,
  };
}

export function getSerializedCohorts(cohortsStore: CohortStore, dataGroupId: string) {
  return sortBy(cohortsStore.value, `name`)
    .filter((cohort: SerializedCohort | SerializedInlineCohort) => {
      const cohortDataGroupId = isSerializedInlineCohort(cohort)
        ? cohort.raw_cohort.data_group_id
        : cohort.data_group_id;
      return dataGroupIDsEqual(dataGroupId, cohortDataGroupId ? cohortDataGroupId.toString() : ``);
    })
    .map((cohort: SerializedCohort | SerializedInlineCohort) => transformCohortToCohortItem(cohort, cohortsStore))
    .filter(Boolean);
}

export function getCohortPropertyItem(name: string, createdBy: User, value: TODO): PropertySectionItem {
  return {
    type: PropertyType.List,
    propertyDefaultType: PropertyType.List,
    resourceType: ResourceType.Cohort,
    label: name,
    value,
    createdBy,
  };
}

export function getDataDefinitionsPropertyMenuItem(propertyDefinition: PropertyDefinition, i18n: LocalizationStore) {
  return {
    ...propertyDefinition,
    label: propertiesUtils.renameProperty(propertyDefinition.name, propertyDefinition.resourceType, i18n),
    value: propertyDefinition.name,
    ...(propertyDefinition.join_property_type ? {joinPropertyType: propertyDefinition.join_property_type} : {}),
    ...(propertyDefinition.dimension_data_group_id
      ? {dimensionDataGroupId: propertyDefinition.dimension_data_group_id}
      : {}),
  };
}

export function isSameProperty(property1: PropertySectionItem, property2: PropertySectionItem): boolean {
  return getItemUniqueKey(property1) === getItemUniqueKey(property2);
}

export function getItemUniqueKey(item: EventSectionItem | PropertySectionItem | Item) {
  // ignore tempDataGroupId and dataGroupId when it's a join key
  const primaryKeys =
    `type` in item && item.type === `dimension`
      ? NonDataGroupPropertyItemIdentifierKeys
      : PropertyItemPersistedIdentifierKeys;
  const primaryKey = primaryKeys.find((key) => Boolean(item[key]));

  if (!(`resourceType` in item) && isEventSectionItem(item)) {
    return item.value.value;
  }
  const {resourceType = ``, value} = item;
  let tempDataGroupId, customPropertyId, dataGroupId;
  if (isProperty(item)) {
    ({tempDataGroupId, customPropertyId = ``, dataGroupId} = item);
  }
  switch (primaryKey) {
    case `tempDataGroupId`:
      return `temp${tempDataGroupId}::${customPropertyId}::${resourceType}::${value}`;
    case `dataGroupId`:
      return `${dataGroupId}::${customPropertyId}::${resourceType}::${value}`;
    case `customPropertyId`:
      return String(customPropertyId);
    default:
      return `${resourceType}::${item[primaryKey]}`;
  }
}

/**
 * Returns a data group id of a given custom property. If it's an event custom property, return null.
 * Otherwise, get the data group id from the composed properties. For profile custom properties, they
 * cannot be consisted of properties from multiple data groups. It can only have properties that belong
 * to the same group (or user)
 * @param {CustomProperty} customProperty
 * @param {ResourceType} resourceType
 * @returns {string | null}
 */
export function getDataGroupIdForCustomProp(customProperty: CustomProperty, resourceType: ResourceType): string | null {
  if (resourceType === ResourceType.Event) {
    return null;
  }

  function getDataGroupId(customProp: CustomProperty): string | null {
    const composedProperties = customProp.composedProperties || {};
    const properties = Object.values(composedProperties);
    for (const prop of properties) {
      if (prop.customProperty) {
        return getDataGroupId(prop.customProperty);
      } else if (prop.dataGroupId && !Array.isArray(prop.value)) {
        // it's a group prop if there's a data group id and if the property value
        // is not an array (if prop value is an array, it's a lookup table property)
        return prop.dataGroupId;
      }
    }
    return null;
  }

  return getDataGroupId(customProperty);
}

export function inferStagedItemType(item: PropertySectionItem, useMultipleSelection: boolean): StagedItemType {
  if (!isProperty(item)) {
    return null;
  }
  if (!useMultipleSelection) {
    if (item.menuItemType === MenuItemType.ComputedListPropertySum) {
      return StagedItemType.ComputedListNumericProperty;
    }

    if (item.menuItemType === MenuItemType.ComputedListPropertyUniques) {
      return StagedItemType.ComputedListProperty;
    }

    if (!item.unit && item.type === PropertyType.Datetime) {
      return StagedItemType.Datetime;
    }

    if (item.type === PropertyType.Dimension) {
      return StagedItemType.Dimension;
    }

    if (item.menuItemType === MenuItemType.BehaviorMultiAttribution) {
      return StagedItemType.BehaviorMultiAttribution;
    }

    if (item.menuItemType === MenuItemType.BorrowProperty) {
      return StagedItemType.BorrowEvent;
    }

    if (item.menuItemType === MenuItemType.EventWithProperties) {
      return StagedItemType.PropertiesForEvent;
    }

    if (
      item.type === PropertyType.Object &&
      item.propertyDefaultType === PropertyType.Object &&
      !item.propertyObjectKey?.length
    ) {
      return StagedItemType.Object;
    }
  }

  if (!isNil(item.stagedItemType)) {
    return item.stagedItemType;
  }
  return null;
}

export function filterToBookmarkablePropertyItem(propertySectionItem: PropertySectionItem): Item {
  const item = produce(propertySectionItem, (itemDraft: PropertySectionItem): void => {
    for (const key in itemDraft) {
      if (!SerializedItemKeyMap[key]) {
        delete itemDraft[key];
      }
    }
  });

  if (propertySectionItem?.value === `$behavior_aggregation`) {
    const userBehavior = {
      aggregationOperator: ALL_MATH_TYPES.TOTAL,
      event: undefined,
      filters: [],
      filtersOperator: FiltersOperator.And,
    };

    const userBehaviorFilter = {
      resourceType: ResourceType.User,
      propertyType: PropertyType.Number,
      propertyDefaultType: PropertyType.Number,
      propertyName: null,
      filterOperator: NumberOperator.AtLeast,
      filterValue: 1,
      dateRange: null,
    };

    const groupBehavior = {
      aggregationOperator: ALL_MATH_TYPES.TOTAL,
      event: undefined,
      filtersOperator: FiltersOperator.And,
      dateRange: null,
      behaviorType: `$behavior_aggregation`,
    };

    const groupFilter = {
      resourceType: ResourceType.User,
      propertyType: PropertyType.Number,
      propertyDefaultType: PropertyType.Number,
    };

    return produce(item, (groupFilterDraft: Property): void => {
      for (const key in groupFilter) {
        if (groupFilterDraft[key] === undefined) {
          groupFilterDraft[key] = groupFilter[key];
        }
      }

      groupFilterDraft.behavior = produce(
        groupFilterDraft.behavior ?? ({} as Behavior),
        (groupBehaviorDraft: Behavior): void => {
          for (const key in groupBehavior) {
            if (groupBehaviorDraft[key] === undefined) {
              groupBehaviorDraft[key] = groupBehavior[key];
            }
          }

          groupBehaviorDraft.filters = produce(
            groupBehaviorDraft.filters ?? ([{}] as Segmentation[]),
            (filtersDraft: Property[]): void => {
              for (const key in userBehaviorFilter) {
                if (filtersDraft[0][key] === undefined) {
                  filtersDraft[0][key] = userBehaviorFilter[key];
                }
              }

              filtersDraft[0].behavior = produce(
                filtersDraft[0].behavior ?? ({} as Behavior),
                (userBehaviorDraft: Behavior): void => {
                  for (const key in userBehavior) {
                    if (userBehaviorDraft[key] === undefined) {
                      userBehaviorDraft[key] = userBehavior[key];
                    }
                  }
                },
              );
            },
          );
        },
      );
    });
  }

  return item;
}

function resourceTypeLabelForPath(resourceType: Property[`resourceType`], dataGroup: DataGroupItem | null) {
  switch (resourceType) {
    case ResourceType.User:
      return dataGroup?.displayName || (({t}) => t(`query-builder.dg-user`));
    case ResourceType.Event:
    case ResourceType.Cohort:
    case ResourceType.Other:
    case ResourceType.RuntimeProperty:
    case ResourceType.Formula:
    case ResourceType.Metric:
    case null:
      return null;
    default:
      assertUnreachable(resourceType);
  }
}

export function getTooltipPathForPropertyItem(
  {value, resourceType}: Property,
  dataGroup: DataGroupItem | null,
  i18n: LocalizationStore,
): Translatable[] {
  // Special case for First Event Time Property - don't prefix with "User >" or data group name because this property
  // is in the computed menu, and it applies to all data groups in the report
  if (value === `$mp_first_event_time`) {
    return [];
  }

  return [
    resourceTypeLabelForPath(resourceType, dataGroup),
    Array.isArray(value) && propertiesUtils.renameProperty(value[0], resourceType, i18n),
  ].filter(Boolean);
}

export function getTooltipNameForPropertyItem({value}: Property): string {
  return Array.isArray(value) ? value[1] : value;
}

/**
 * This function is to ensure consistency between the properties-menu fetch
 * call and the pre-fetch call made in query-builder
 * */
export function getEventPropertyDefinitionsParams(
  {
    projectId,
    workspaceId,
    event,
    includeDimensionProperties,
  }: {projectId: string | number; workspaceId: string | number; event: string[]; includeDimensionProperties: boolean},
  includeAllProperties = false,
) {
  return {
    ...(includeAllProperties ? {} : {event}),
    projectId,
    workspaceId,
    includeDimensionProperties,
    includeHidden: true,
    includeDensity: true,
  };
}

// This function fetches and populates the Lookup Table Store with each table's properties
export function fetchAllLookupTableProperties(i18n: LocalizationStore) {
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  for (const table of Object.values(lookupTablesStore.value)) {
    if (table.hasMappedProperties) {
      const dataGroupId = table[`id`];
      fetchLookupTableProperties(dataGroupId, projectId, workspaceId, i18n);
    }
  }
}

type SelectedLabelParams = {
  selected: TODO;
  showSpecialDatePropertyUnit?: boolean;
  cohortsStore?: CohortStore;
  dataGroups: DataGroup[];
  expandListProperties?: boolean;
};

export function getSelectedPropertyLabel({
  selected,
  showSpecialDatePropertyUnit = false,
  cohortsStore,
  dataGroups,
  expandListProperties = true,
}: SelectedLabelParams): Translatable {
  if (!selected) {
    return ``;
  }

  const selectedItem = Array.isArray(selected) ? selected[0] : selected;
  const dataGroupId = isProperty(selectedItem) ? selectedItem.dataGroupId : null;

  if (selected.renamed) {
    return `${selected.renamed} (${selected.label})`;
  } else if (!selected.value && selected.label) {
    return selected.label;
  } else if (selected.behavior) {
    return getLabelForBehavior(selected.behavior, dataGroupId, dataGroups);
  }
  if (!selectedItem) {
    return ``;
  }

  // use canonical cohort names when available
  if (selectedItem.resourceType === ResourceType.Cohort) {
    if (Array.isArray(selected)) {
      const length = selected.length;
      return ({t}) => t(`query-builder.cohorts.count`, {count: length});
    }

    const label = cohortsStore?.value[selectedItem.value]?.name || selectedItem.label;
    return selectedItem.negated ? ({t}) => t(`query-builder.cohorts.not-in-cohort`, {cohortName: label}) : label;
  }

  return getSelectedPropertyName({
    selected,
    showSpecialDatePropertyUnit,
    expandListProperties,
    dataGroupId,
  });
}

export function getSelectedLabel(params: SelectedLabelParams): Translatable {
  const {selected} = params;
  const selectedItem = Array.isArray(selected) ? selected[0] : selected;

  if (!selectedItem) {
    return ({t}) => t(`query-builder.select-property`);
  }

  /** allows for passing selected events to the properties menu */
  if ((selectedItem as Property).menuItemType === MenuItemType.Events) {
    return getEventSelectedLabel(selectedItem as Event);
  }

  const propertyLabel = getSelectedPropertyLabel(params);

  if (`borrowEvent` in selectedItem) {
    const eventLabel = getEventSelectedLabel(selectedItem.borrowEvent);
    return ({print}) => `Borrow from ${print(eventLabel)} → ${print(propertyLabel)}`;
  }

  return propertyLabel || (({t}) => t(`query-builder.select-property`));
}

export function getSelectedLeftIcon(selected: Selection): string | null {
  const item = Array.isArray(selected) ? selected[0] : selected;

  if (`borrowEvent` in selected) {
    return null;
  }

  const resourceType = item?.resourceType;

  switch (resourceType) {
    case ResourceType.User: {
      return isProperty(item) && item.dataGroupId ? `group-attribute` : `profile-attribute`;
    }
    case ResourceType.Event:
      return `event-attribute`;
    case ResourceType.Cohort:
      return `community`;
    default: {
      // special prop icons
      if (item?.value === TOP_PEOPLE.value) {
        return TOP_PEOPLE.icon;
      }
      return null;
    }
  }
}

/**
 * @deprecated
 * This is currently only used to getTooltip data.
 * We should be able to refactor this out soon.
 */
export function getPropertyDefinitions(
  resourceTypes: ResourceType[],
  customProperties: CustomPropertiesResource,
  inlineCustomProperties: InlineCustomPropertiesStore,
) {
  return resourceTypes.reduce((acc, type) => {
    let definitions: PropertyDefinition[] = [];
    switch (type) {
      case ResourceType.Event:
        // ? Query block is only able to edit global definitions
        // ? we need to keep serving tooltip data out of global definitions until this changes
        definitions = getAllGlobalPropertyDefinitions();
        break;
      case ResourceType.User:
        definitions = getAllPropertyDefinitionsForUserProfiles().concat(getAllPropertyDefinitionsForGroupProfiles());
        break;
    }

    const allCustomProperties = inlineCustomProperties.layerOnto(customProperties);
    const allCustomPropertiesKeys = Array.from(allCustomProperties.keys());
    const inlineCustomPropertyIds = allCustomPropertiesKeys.filter((customPropertyId) => {
      return (
        !isPersistedCustomProperty(customPropertyId) &&
        // this filters out the copy that we put into the model resource before we saved it
        !allCustomProperties.get(customPropertyId)?.newCustomPropertyId
      );
    });

    const customPropertyIdSet = new Set(definitions.map((d) => d.customPropertyId).filter((id) => Boolean(id)));
    const createdCustomPropertyIds = allCustomPropertiesKeys.filter(
      (customPropertyId) => isPersistedCustomProperty(customPropertyId) && !customPropertyIdSet.has(customPropertyId),
    );

    // add in definitions from newly created and inline custom properties
    const customDefinitions = [...createdCustomPropertyIds, ...inlineCustomPropertyIds]
      .map((customPropertyId) => {
        const customProperty = allCustomProperties.get(customPropertyId);
        const property = storeCustomPropertyToProperty(customPropertyId, customProperty);
        return {
          ...property,
          name: customProperty?.name,
          description: customProperty?.description,
          status: `custom`,
        } as PropertyDefinition;
      })
      .filter(matchesProperty(`resourceType`, type));

    const regularDefinitions = definitions.filter((definition) => definition.status !== `custom`);

    acc[type] = [...customDefinitions, ...regularDefinitions, ...EXTRA_PROPERTY_DEFINITIONS_BY_TYPE[type]];

    return acc;
  }, {});
}

function createSerializedCohorts({
  cohortName,
  cohort,
  createdBy,
  unsavedId,
}: {
  cohortName: string;
  cohort: CohortModel;
  createdBy: SerializedInlineCohort[`created_by`];
  unsavedId: string;
}): SerializedInlineCohort {
  return {
    name: cohortName,
    raw_cohort: {
      ...cohort.serialize(),
      unsavedId,
      ...(createdBy ? {createdBy} : {}),
    },
  } as SerializedInlineCohort;
}

/**
 * Adding a temporary cohort, editing a temporary cohort, saving a new cohort directly,
 * or saving a temporary cohort
 * @param {CohortStore} [cohortsStore]
 * @param {User} [createdBy]
 * @param {object} [options]
 * @param {number} [options.id] - id of the cohort, only applicable if saving the cohort
 * @param {CohortModel} [options.cohort] - cohort being passed in, only exists if not saving the cohort
 * @param {string} [options.name] - name of the cohort
 * @param {boolean} [options.savedAsNew] - whether we are saving the cohort as new
 *
 * Adding a new temporary cohort - name, cohort
 * []Editing a temporary cohort - name, cohort
 * []Saving an existing temporary cohort - id, name, cohort
 * Saving a new cohort / editing an existing cohort - id, name, cohort, savedAsNew = false
 * Saving an existing cohort as new - id, name, cohort, savedAsNew = true
 */
export function commitCohort(
  cohortsStore: CohortStore,
  createdBy: User,
  {id, cohort, name, savedAsNew}: CohortResultItem,
): {
  commitedItem: PropertySectionItem;
  isSaved: boolean;
  unsavedId: string;
  isEditingTemporaryCohort: boolean;
  isEditingExistingCohort: boolean;
} {
  const isSaved = Boolean(id);
  const isEditingTemporaryCohort = Boolean(cohort.unsavedId);
  const unsavedId = cohort.unsavedId || nanoid();

  let itemCohort: SerializedInlineCohort;
  if (!isSaved) {
    itemCohort = createSerializedCohorts({
      cohortName: name,
      cohort,
      createdBy,
      unsavedId,
    });
    // updating cohort store for cohorts that are temporary
    cohortsStore.update({
      [unsavedId]: {
        name,
        raw_cohort: itemCohort.raw_cohort,
        created_by: cohort.createdBy,
      } as SerializedInlineCohort,
    });
  } else if (isEditingTemporaryCohort && isSaved) {
    // Mark unsaved cohort being converted to a saved cohort
    cohortsStore.update({
      [unsavedId]: {
        name,
        created_by: cohort.createdBy,
        ...(cohortsStore.value[unsavedId] || {}),
        id: cohort.id,
      } as SerializedInlineCohort,
    });
  }

  const cohortItem = isSaved
    ? getCohortPropertyItem(name, createdBy, id)
    : getCohortPropertyItem(name, createdBy, {cohort: itemCohort});

  return {
    commitedItem: cohortItem,
    isSaved,
    unsavedId,
    isEditingTemporaryCohort,
    isEditingExistingCohort: unsavedId === null && savedAsNew,
  };
}
